import { z } from 'zod';

import {
  DEFAULT_FORMATION_RULES,
  DEFAULT_SCORING_RULES,
  type FormationRules,
} from '@scoring/rules.ts';
import type { Json } from '@/lib/supabase/types';

const positionCountsSchema = z.object({
  GK: z.number().int().positive(),
  DEF: z.number().int().positive(),
  MID: z.number().int().positive(),
  FWD: z.number().int().positive(),
});

const rangeSchema = z.tuple([z.number().int().nonnegative(), z.number().int().positive()]);

const formationRulesSchema = z.object({
  squad: positionCountsSchema,
  starting: z.object({
    size: z.number().int().positive(),
    GK: rangeSchema,
    DEF: rangeSchema,
    MID: rangeSchema,
    FWD: rangeSchema,
  }),
  scoring: z
    .object({
      minutes: z.object({ played: z.number(), played60: z.number() }),
      goal: positionCountsSchema.partial().passthrough(),
      assist: z.number(),
      captainMultiplier: z.number(),
      bonus: z.array(z.number()),
    })
    .passthrough(),
});

/**
 * Parse `fantasy_seasons.formation_rules` defensively: any malformed or
 * missing payload falls back to the canonical defaults so the builder never
 * renders with broken constraints.
 */
export function parseFormationRules(value: Json | null | undefined): FormationRules {
  const result = formationRulesSchema.safeParse(value);
  if (!result.success) {
    return DEFAULT_FORMATION_RULES;
  }
  const parsed = result.data;
  return {
    squad: parsed.squad,
    starting: parsed.starting,
    scoring: {
      ...DEFAULT_SCORING_RULES,
      ...parsed.scoring,
      goal: { ...DEFAULT_SCORING_RULES.goal, ...parsed.scoring.goal },
    },
  };
}
